import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { daysBetween, formatDate, formatRelative, todayIso } from '../lib/dates';
import {
  deletePhoto,
  formatBytes,
  listPhotos,
  savePhoto,
  totalBytes,
  type ProgressPhoto,
} from '../lib/photos';
import { useApp } from '../store/AppStore';
import { IconPlus } from './Icons';
import { Sheet } from './Sheet';

export function ProgressPhotos() {
  const { state } = useApp();
  const [photos, setPhotos] = useState<ProgressPhoto[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [compare, setCompare] = useState(false);
  const [viewing, setViewing] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    try {
      setPhotos(await listPhotos());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read saved photos.');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Object URLs are a manual resource — leak them and the blobs stay in memory
  // for the life of the page.
  useEffect(() => {
    const next: Record<string, string> = {};
    for (const photo of photos) next[photo.id] = URL.createObjectURL(photo.blob);
    setUrls(next);
    return () => {
      for (const url of Object.values(next)) URL.revokeObjectURL(url);
    };
  }, [photos]);

  const onFiles = async (files: FileList) => {
    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        await savePhoto(file, { date: todayIso(), weightKg: state.profile.bodyweightKg });
      }
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that photo.');
    } finally {
      setBusy(false);
    }
  };

  const newest = photos[photos.length - 1];
  const oldest = photos[0];
  const spanDays = oldest && newest ? daysBetween(oldest.date, newest.date) : 0;

  return (
    <div className="card">
      <div className="row between" style={{ marginBottom: 10 }}>
        <span className="card-title" style={{ margin: 0 }}>
          Progress photos
        </span>
        {photos.length >= 2 && (
          <button className="btn ghost sm" onClick={() => setCompare(true)}>
            Compare
          </button>
        )}
      </div>

      {error && <div className="warning" style={{ marginBottom: 10 }}>{error}</div>}

      {photos.length === 0 ? (
        <p className="small muted">
          Same pose, same spot, same light, once a week. Over a fat-loss phase these
          tell you far more than the scale does — weight swings a kilo a day on water
          and food, but a photo eight weeks apart doesn't lie.
        </p>
      ) : (
        <>
          <div className="photo-grid">
            {[...photos].reverse().map((photo) => (
              <button
                className="photo-cell"
                key={photo.id}
                onClick={() => setViewing(photo.id)}
                aria-label={`Photo from ${formatDate(photo.date)}`}
              >
                {urls[photo.id] && <img src={urls[photo.id]} alt="" loading="lazy" />}
                <span className="photo-date">{formatDate(photo.date)}</span>
              </button>
            ))}
          </div>
          <div className="tiny faint" style={{ marginTop: 9 }}>
            {photos.length} photo{photos.length === 1 ? '' : 's'}
            {spanDays > 0 && ` over ${spanDays} days`} · {formatBytes(totalBytes(photos))} on this
            device
          </div>
        </>
      )}

      <button
        className="btn block"
        style={{ marginTop: 11 }}
        disabled={busy}
        onClick={() => fileInput.current?.click()}
      >
        <IconPlus /> {busy ? 'Saving…' : 'Add photo'}
      </button>
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files?.length) void onFiles(e.target.files);
          e.target.value = '';
        }}
      />

      <div className="tiny faint" style={{ marginTop: 9 }}>
        Photos stay on this device and are not included in the JSON backup — clearing
        your browser data deletes them.
      </div>

      <CompareSheet open={compare} photos={photos} urls={urls} onClose={() => setCompare(false)} />

      <ViewSheet
        photo={photos.find((p) => p.id === viewing)}
        url={viewing ? urls[viewing] : undefined}
        onClose={() => setViewing(null)}
        onDelete={async (id) => {
          await deletePhoto(id);
          setViewing(null);
          await refresh();
        }}
      />
    </div>
  );
}

function CompareSheet({
  open,
  photos,
  urls,
  onClose,
}: {
  open: boolean;
  photos: ProgressPhoto[];
  urls: Record<string, string>;
  onClose: () => void;
}) {
  // Default to the widest span available: first versus latest.
  const [leftId, setLeftId] = useState<string | undefined>();
  const [rightId, setRightId] = useState<string | undefined>();

  const left = photos.find((p) => p.id === leftId) ?? photos[0];
  const right = photos.find((p) => p.id === rightId) ?? photos[photos.length - 1];

  const gap = useMemo(() => {
    if (!left || !right) return 0;
    return Math.abs(daysBetween(left.date, right.date));
  }, [left, right]);

  const weightDelta =
    left?.weightKg && right?.weightKg
      ? Math.round((right.weightKg - left.weightKg) * 10) / 10
      : undefined;

  if (!open || !left || !right) return null;

  return (
    <Sheet open title="Compare" onClose={onClose}>
      <div className="compare">
        {[left, right].map((photo, i) => (
          <div key={`${photo.id}-${i}`}>
            <div className="frame" style={{ aspectRatio: '3 / 4' }}>
              {urls[photo.id] && <img src={urls[photo.id]} alt="" />}
            </div>
            <div className="tiny center muted" style={{ marginTop: 5 }}>
              {formatDate(photo.date)}
              {photo.weightKg ? ` · ${photo.weightKg} kg` : ''}
            </div>
          </div>
        ))}
      </div>

      <div className="row between" style={{ marginTop: 14 }}>
        <span className="pill">{gap} days apart</span>
        {weightDelta !== undefined && (
          <span className={`pill ${weightDelta < 0 ? 'ok' : weightDelta > 0 ? 'warn' : ''}`}>
            {weightDelta > 0 ? '+' : ''}
            {weightDelta} kg
          </span>
        )}
      </div>

      <div className="grid-2" style={{ marginTop: 14 }}>
        <div className="field">
          <label htmlFor="cmp-left">Left</label>
          <select id="cmp-left" value={left.id} onChange={(e) => setLeftId(e.target.value)}>
            {photos.map((p) => (
              <option key={p.id} value={p.id}>
                {formatDate(p.date)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="cmp-right">Right</label>
          <select id="cmp-right" value={right.id} onChange={(e) => setRightId(e.target.value)}>
            {photos.map((p) => (
              <option key={p.id} value={p.id}>
                {formatDate(p.date)}
              </option>
            ))}
          </select>
        </div>
      </div>
    </Sheet>
  );
}

function ViewSheet({
  photo,
  url,
  onClose,
  onDelete,
}: {
  photo: ProgressPhoto | undefined;
  url: string | undefined;
  onClose: () => void;
  onDelete: (id: string) => Promise<void>;
}) {
  if (!photo) return null;

  return (
    <Sheet open title={formatRelative(photo.date)} onClose={onClose}>
      <div className="frame" style={{ aspectRatio: '3 / 4' }}>
        {url && <img src={url} alt="" />}
      </div>
      <div className="row between" style={{ marginTop: 12 }}>
        <span className="muted small">{formatDate(photo.date)}</span>
        {photo.weightKg && <span className="pill">{photo.weightKg} kg</span>}
      </div>
      <button
        className="btn danger block"
        style={{ marginTop: 16 }}
        onClick={() => {
          if (confirm('Delete this photo? It cannot be recovered.')) void onDelete(photo.id);
        }}
      >
        Delete photo
      </button>
    </Sheet>
  );
}
